/**
 * MKS-328 — money formatting for the Manager Overview surface.
 *
 * Every figure this page renders comes from `revenueService.getRevenue`'s
 * `consumption`, which is CZK by construction: the billing ledger carries no
 * currency column and foreign rates are converted to CZK at write time (see
 * `docs/private/adr/0003-czk-denominated-billing-ledger.md`). The page nonetheless
 * rendered them as `€{N}k` — a "bundle convention" inherited from the design
 * mock-up, so staging showed "€24.3k" against 24 300 CZK of real revenue.
 *
 * `formatPortfolioMoney` routes to the shared `formatCurrency` helper every
 * other money surface uses (`1 834 200 Kč`, Czech grouping, no decimals —
 * these are headline figures, not invoice lines).
 *
 * When MKS-314 introduces a company reporting currency, `formatCurrency`'s
 * `currency` argument is the single place that has to learn about it.
 */
import { formatCurrency } from '../../utils/format';

/**
 * The page's money formatter. It never compacts: a manager comparing a row
 * against an invoice needs the real figure, and `formatCurrency` already
 * groups it readably.
 */
export function formatPortfolioMoney(value: number): string {
  return formatCurrency(value, false);
}

/**
 * MKS-328 — the revenue-weighted margin of a set of rows:
 * (Σ revenue − Σ cost) / Σ revenue, the same formula the backend applies per
 * project and for the Avg margin KPI. Null when the set has no positive
 * revenue to weigh against, so the cell reads "—" instead of a nonsense
 * percentage. Rows with no positive revenue are excluded, so a cost-only
 * internal project cannot drag a group negative without contributing any
 * revenue of its own.
 */
export function weightedMargin(
  rows: Array<{ revenue: number; cost: number }>
): number | null {
  const earning = rows.filter((r) => r.revenue > 0);
  const revenue = earning.reduce((sum, r) => sum + r.revenue, 0);
  const cost = earning.reduce((sum, r) => sum + r.cost, 0);
  return revenue <= 0 ? null : (revenue - cost) / revenue;
}

/** Margin as a whole percentage, "—" when there is none. */
export function formatMargin(margin: number | null): string {
  if (margin === null) return '—';
  return `${Math.round(margin * 100)}%`;
}

/** Tailwind text colour for a margin cell: red below 0, amber below 20%. */
export function marginToneClass(margin: number | null): string {
  if (margin === null) return 'text-ms-text-muted';
  if (margin < 0) return 'text-red-600';
  if (margin < 0.2) return 'text-amber-700';
  return 'text-emerald-600';
}
